import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  Plus, 
  Search, 
  ClipboardList,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Send,
  RefreshCw,
  TrendingUp,
  Calendar,
  Trash2,
  Eye,
  Bell
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  pending: { label: "En attente", color: "bg-yellow-100 text-yellow-700", icon: Clock },
  sent: { label: "Envoyée", color: "bg-blue-100 text-blue-700", icon: Send },
  received: { label: "Reçue", color: "bg-green-100 text-green-700", icon: CheckCircle },
  no_response: { label: "Sans réponse", color: "bg-orange-100 text-orange-700", icon: AlertTriangle },
  declined: { label: "Déclinée", color: "bg-red-100 text-red-700", icon: XCircle }
};

export default function SupplierTracking() {
  const { getAuthHeaders } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [projects, setProjects] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [pendingReminders, setPendingReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterProject, setFilterProject] = useState("all");
  const [addOpen, setAddOpen] = useState(false);
  const [updateOpen, setUpdateOpen] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  
  const [newQuote, setNewQuote] = useState({
    project_id: "",
    supplier_id: "",
    request_message: "",
    items: []
  });

  const [updateData, setUpdateData] = useState({
    status: "",
    response_total: "",
    response_delay_days: "",
    response_conditions: "",
    response_notes: ""
  });

  const fetchData = useCallback(async () => {
    try {
      const [quotesRes, projectsRes, suppliersRes, remindersRes] = await Promise.all([
        axios.get(`${API}/supplier-quotes`, { headers: getAuthHeaders() }),
        axios.get(`${API}/projects`, { headers: getAuthHeaders() }),
        axios.get(`${API}/suppliers`, { headers: getAuthHeaders() }),
        axios.get(`${API}/supplier-quotes/pending-reminders`, { headers: getAuthHeaders() })
      ]);
      setQuotes(quotesRes.data);
      setProjects(projectsRes.data);
      setSuppliers(suppliersRes.data);
      setPendingReminders(remindersRes.data.quotes_to_remind || []);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAdd = async (e) => {
    e.preventDefault();
    
    if (!newQuote.project_id || !newQuote.supplier_id) {
      toast.error("Veuillez sélectionner un projet et un fournisseur");
      return;
    }
    
    try {
      await axios.post(
        `${API}/supplier-quotes`,
        newQuote,
        { headers: getAuthHeaders() }
      );
      toast.success("Demande de devis créée");
      setAddOpen(false);
      setNewQuote({ project_id: "", supplier_id: "", request_message: "", items: [] });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la création");
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    
    if (!selectedQuote) return;
    
    const data = {};
    if (updateData.status) data.status = updateData.status;
    if (updateData.response_total) data.response_total = parseFloat(updateData.response_total);
    if (updateData.response_delay_days) data.response_delay_days = parseInt(updateData.response_delay_days);
    if (updateData.response_conditions) data.response_conditions = updateData.response_conditions;
    if (updateData.response_notes) data.response_notes = updateData.response_notes;
    
    try {
      await axios.put(
        `${API}/supplier-quotes/${selectedQuote.id}`,
        data,
        { headers: getAuthHeaders() }
      );
      toast.success("Demande mise à jour");
      setUpdateOpen(false);
      setSelectedQuote(null);
      setUpdateData({ status: "", response_total: "", response_delay_days: "", response_conditions: "", response_notes: "" });
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleSendReminder = async (quoteId) => {
    try {
      await axios.post(
        `${API}/supplier-quotes/${quoteId}/send-reminder`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success("Relance envoyée");
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de l'envoi de la relance");
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    
    try {
      await axios.delete(`${API}/supplier-quotes/${deleteId}`, { headers: getAuthHeaders() });
      toast.success("Demande supprimée");
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    } finally {
      setDeleteId(null);
    }
  };

  const openUpdateDialog = (quote) => {
    setSelectedQuote(quote);
    setUpdateData({
      status: quote.status,
      response_total: quote.response_total?.toString() || "",
      response_delay_days: quote.response_delay_days?.toString() || "",
      response_conditions: quote.response_conditions || "",
      response_notes: quote.response_notes || ""
    });
    setUpdateOpen(true);
  };

  const filteredQuotes = quotes.filter(q => {
    const matchesSearch = q.supplier_name.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = filterStatus === "all" || q.status === filterStatus;
    const matchesProject = filterProject === "all" || q.project_id === filterProject;
    return matchesSearch && matchesStatus && matchesProject;
  });

  const formatCurrency = (amount) => {
    if (!amount) return "-";
    return new Intl.NumberFormat('fr-CH', { style: 'currency', currency: 'CHF' }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString('fr-CH', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getDaysWaiting = (requestDate) => {
    const diff = Date.now() - new Date(requestDate).getTime();
    return Math.floor(diff / (1000 * 60 * 60 * 24));
  };

  // Stats
  const stats = {
    total: quotes.length,
    pending: quotes.filter(q => q.status === "pending" || q.status === "sent").length,
    received: quotes.filter(q => q.status === "received").length,
    toRemind: pendingReminders.length
  };

  return (
    <Layout>
      <div className="space-y-6 fade-in">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-gray-900">
              Suivi des demandes fournisseurs
            </h1>
            <p className="text-gray-500 mt-1">
              Centralisez et comparez les offres reçues
            </p>
          </div>
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary gap-2" data-testid="new-quote-request-btn">
                <Plus className="h-5 w-5" />
                Nouvelle demande
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Nouvelle demande de devis</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAdd} className="space-y-4">
                <div className="space-y-2">
                  <Label>Projet *</Label>
                  <Select 
                    value={newQuote.project_id}
                    onValueChange={(val) => setNewQuote({ ...newQuote, project_id: val })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner un projet..." />
                    </SelectTrigger>
                    <SelectContent>
                      {projects.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Fournisseur *</Label>
                  <Select 
                    value={newQuote.supplier_id}
                    onValueChange={(val) => setNewQuote({ ...newQuote, supplier_id: val })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner un fournisseur..." />
                    </SelectTrigger>
                    <SelectContent>
                      {suppliers.map(s => (
                        <SelectItem key={s.id} value={s.id}>{s.name} ({s.category})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Message (optionnel)</Label>
                  <Textarea
                    value={newQuote.request_message}
                    onChange={(e) => setNewQuote({ ...newQuote, request_message: e.target.value })}
                    placeholder="Détails de la demande..."
                    rows={3}
                  />
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setAddOpen(false)}>
                    Annuler
                  </Button>
                  <Button type="submit" className="btn-primary">
                    Créer
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="stat-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total demandes</p>
                  <p className="text-2xl font-bold">{stats.total}</p>
                </div>
                <ClipboardList className="h-8 w-8 text-gray-300" />
              </div>
            </CardContent>
          </Card>
          <Card className="stat-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">En attente</p>
                  <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
                </div>
                <Clock className="h-8 w-8 text-yellow-300" />
              </div>
            </CardContent>
          </Card>
          <Card className="stat-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Reçues</p>
                  <p className="text-2xl font-bold text-green-600">{stats.received}</p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-300" />
              </div>
            </CardContent>
          </Card>
          <Card className="stat-card border-orange-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">À relancer</p>
                  <p className="text-2xl font-bold text-orange-600">{stats.toRemind}</p>
                </div>
                <Bell className="h-8 w-8 text-orange-300" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Alerts for reminders */}
        {pendingReminders.length > 0 && (
          <Card className="border-orange-200 bg-orange-50">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-orange-600 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-orange-800">
                    {pendingReminders.length} demande{pendingReminders.length > 1 ? "s" : ""} sans réponse depuis plus de 10 jours
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {pendingReminders.slice(0, 3).map(q => (
                      <Badge key={q.id} variant="outline" className="bg-white">
                        {q.supplier_name} ({q.days_waiting} jours)
                      </Badge>
                    ))}
                    {pendingReminders.length > 3 && (
                      <Badge variant="outline" className="bg-white">
                        +{pendingReminders.length - 3} autres
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Rechercher un fournisseur..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les statuts</SelectItem>
              {Object.entries(STATUS_CONFIG).map(([key, val]) => (
                <SelectItem key={key} value={key}>{val.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterProject} onValueChange={setFilterProject}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Projet" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les projets</SelectItem>
              {projects.map(p => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-12 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-4 border-[#1E8B5A] border-t-transparent mx-auto" />
              </div>
            ) : filteredQuotes.length === 0 ? (
              <div className="p-12 text-center">
                <ClipboardList className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="font-medium text-gray-900 mb-2">Aucune demande</h3>
                <p className="text-sm text-gray-500">
                  Créez votre première demande de devis fournisseur
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fournisseur</TableHead>
                    <TableHead>Projet</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Demandé le</TableHead>
                    <TableHead>Délai</TableHead>
                    <TableHead className="text-right">Montant</TableHead>
                    <TableHead>Relances</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredQuotes.map((quote) => {
                    const StatusIcon = STATUS_CONFIG[quote.status]?.icon || Clock;
                    const daysWaiting = getDaysWaiting(quote.request_date);
                    const projectName = projects.find(p => p.id === quote.project_id)?.name || "-";
                    
                    return (
                      <TableRow key={quote.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{quote.supplier_name}</p>
                            <p className="text-xs text-gray-500">{quote.supplier_email}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-gray-600">{projectName}</TableCell>
                        <TableCell>
                          <Badge className={STATUS_CONFIG[quote.status]?.color}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {STATUS_CONFIG[quote.status]?.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p>{formatDate(quote.request_date)}</p>
                            {quote.status !== "received" && (
                              <p className={`text-xs ${daysWaiting > 10 ? 'text-orange-600' : 'text-gray-500'}`}>
                                {daysWaiting} jour{daysWaiting > 1 ? "s" : ""}
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {quote.response_delay_days 
                            ? `${quote.response_delay_days} jours` 
                            : "-"}
                        </TableCell>
                        <TableCell className="text-right font-semibold">
                          {formatCurrency(quote.response_total)}
                        </TableCell>
                        <TableCell>
                          {quote.reminder_count > 0 ? (
                            <Badge variant="outline">{quote.reminder_count}</Badge>
                          ) : "-"}
                        </TableCell>
                        <TableCell>
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openUpdateDialog(quote)}
                              className="h-8 w-8"
                              title="Modifier"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            {(quote.status === "pending" || quote.status === "sent") && daysWaiting >= 10 && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleSendReminder(quote.id)}
                                className="h-8 w-8 text-orange-600"
                                title="Envoyer une relance"
                              >
                                <RefreshCw className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setDeleteId(quote.id)}
                              className="h-8 w-8 text-gray-400 hover:text-red-500"
                              title="Supprimer"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Update Dialog */}
        <Dialog open={updateOpen} onOpenChange={setUpdateOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Mettre à jour la réponse</DialogTitle>
            </DialogHeader>
            {selectedQuote && (
              <form onSubmit={handleUpdate} className="space-y-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="font-medium">{selectedQuote.supplier_name}</p>
                  <p className="text-sm text-gray-500">{selectedQuote.supplier_email}</p>
                </div>
                
                <div className="space-y-2">
                  <Label>Statut</Label>
                  <Select 
                    value={updateData.status}
                    onValueChange={(val) => setUpdateData({ ...updateData, status: val })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(STATUS_CONFIG).map(([key, val]) => (
                        <SelectItem key={key} value={key}>{val.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Montant total (CHF)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={updateData.response_total}
                      onChange={(e) => setUpdateData({ ...updateData, response_total: e.target.value })}
                      placeholder="0.00"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Délai (jours)</Label>
                    <Input
                      type="number"
                      value={updateData.response_delay_days}
                      onChange={(e) => setUpdateData({ ...updateData, response_delay_days: e.target.value })}
                      placeholder="Ex: 14"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Conditions</Label>
                  <Input
                    value={updateData.response_conditions}
                    onChange={(e) => setUpdateData({ ...updateData, response_conditions: e.target.value })}
                    placeholder="Ex: Livraison franco, paiement 30 jours..."
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea
                    value={updateData.response_notes}
                    onChange={(e) => setUpdateData({ ...updateData, response_notes: e.target.value })}
                    placeholder="Remarques supplémentaires..."
                    rows={2}
                  />
                </div>
                
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setUpdateOpen(false)}>
                    Annuler
                  </Button>
                  <Button type="submit" className="btn-primary">
                    Enregistrer
                  </Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Dialog */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Supprimer cette demande ?</AlertDialogTitle>
              <AlertDialogDescription>
                Cette action est irréversible.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete}
                className="bg-red-500 hover:bg-red-600"
              >
                Supprimer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
